import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { decideApproval, decideApprovalModified } from '@/lib/approvalsData';
import ApprovalCard from '@/components/dashboard/ApprovalCard';

/* The approval inbox, beside the conversation. Same card, same confirm
 * step and the same audited kc_approval_decide RPC as the full inbox page;
 * the console never writes tables directly. */

export default function ConsoleInbox({ approvals, onChanged, t }) {
  const { pending = [], decidedCount = 0, previews = {}, postPreviews = {}, loading, error } = approvals;
  const [confirming, setConfirming] = useState(null); // { row, decision, edits }
  const [busyId, setBusyId] = useState(null);
  const [decideError, setDecideError] = useState(null);

  const confirmDecision = async () => {
    const { row, decision, edits } = confirming || {};
    setConfirming(null);
    if (!row) return;
    setBusyId(row.id);
    setDecideError(null);
    try {
      if (decision === 'modified') await decideApprovalModified(row.id, edits);
      else await decideApproval(row.id, decision);
      await onChanged?.();
    } catch (e) {
      setDecideError(e?.message || t('approvals.decideError'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section data-testid="milli-console-inbox">
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-stone-500">
          {t('milli.console.inbox')}
          {pending.length ? (
            <span className="ml-2 rounded-full bg-amber-400/15 border border-amber-400/40 px-1.5 py-0.5 font-mono text-[10px] text-amber-300" data-testid="milli-inbox-count">
              {pending.length}
            </span>
          ) : null}
        </span>
        <Link
          to="/dashboard/approvals"
          className="font-mono text-[10px] uppercase tracking-wider text-stone-500 hover:text-[color:var(--milli-accent)] transition-colors"
          data-testid="milli-inbox-full"
        >
          {t('milli.console.inboxFull')}
        </Link>
      </div>

      {error || decideError ? (
        <p className="mx-4 mb-2 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300" data-testid="milli-inbox-error">
          {decideError || error}
        </p>
      ) : null}

      <div className="px-4 pb-4 space-y-3">
        {loading && pending.length === 0 ? (
          <p className="text-xs text-stone-500 animate-pulse">…</p>
        ) : pending.length === 0 ? (
          <div className="rounded-lg border border-[#3a332a] px-4 py-6 text-center" data-testid="milli-inbox-empty">
            <ShieldCheck size={20} className="mx-auto mb-2 text-stone-600" />
            <p className="text-xs text-stone-500">{t('milli.console.inboxEmpty')}</p>
            {decidedCount ? (
              <p className="mt-1 font-mono text-[10px] text-stone-600">
                {t('milli.console.inboxDecided', { n: decidedCount })}
              </p>
            ) : null}
          </div>
        ) : (
          pending.map((row) => (
            <ApprovalCard
              key={row.id}
              row={row}
              previews={previews[row.id]}
              postPreview={postPreviews[row.id]}
              busy={busyId === row.id}
              onDecide={(r, decision, edits) => setConfirming({ row: r, decision, edits })}
              t={t}
              compact
            />
          ))
        )}
      </div>

      <AlertDialog
        open={!!confirming}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
      >
        <AlertDialogContent data-testid="approvals-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirming?.decision === 'approved'
                ? t('approvals.confirmApproveTitle')
                : confirming?.decision === 'modified'
                  ? t('approvals.confirmModifiedTitle')
                  : t('approvals.confirmRejectTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirming?.row?.decision_needed}
              {confirming?.decision === 'approved'
                ? `. ${t('approvals.confirmApproveBody')}`
                : confirming?.decision === 'modified'
                  ? `. ${t('approvals.confirmModifiedBody')}`
                  : `. ${t('approvals.confirmRejectBody')}`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('approvals.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDecision}
              className={
                confirming?.decision === 'rejected'
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }
              data-testid="approvals-confirm-action"
            >
              {confirming?.decision === 'approved'
                ? t('approvals.approve')
                : confirming?.decision === 'modified'
                  ? t('approvals.approveEdited')
                  : t('approvals.reject')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
